import { essentialFormApiConnector } from 'apiConnector/form/essential';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { EssentialInitFormRequest } from 'models/forms/essential';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { defaultFormReceivedThunk } from 'state/essential/thunks';
import { commonFormActions } from 'state/forms/actions/form';
import { getUserPreferencesCurrency, getUserPreferencesXMargin, isUserPreferencesPrivateMode } from 'state/selectors';
import type { EpicWithThunk } from 'state/state.common';
import { getTodayStringInLocalTimezone } from 'utils/core/date';
import { HttpError } from 'utils/http/httpClient.model';

export function fetchDefaultEssentialFormEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, state$, { httpClient }) => {
    const productSelectedTabId$: Observable<EssentialInitFormRequest> = action$.pipe(
      ofType('WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED'),
      filter(action => action.workspaceType === 'essential'),
      withLatestFrom(state$),
      map(([action, state]) => {
        return {
          tabId: action.tabId,
          tradeDate: getTodayStringInLocalTimezone(),
          productType: action.productVariation.productType,
          productSubType: action.productVariation.productSubType,
          wrapper: action.productVariation.wrapper,
          isPrivateMode: isUserPreferencesPrivateMode(state),
          currency: getUserPreferencesCurrency(state),
          selectedOnBehalfClient: action.client ?? undefined,
          xMargin: getUserPreferencesXMargin(state),
        };
      }),
    );

    const resetTabId$: Observable<EssentialInitFormRequest> = action$.pipe(
      ofType('WORKSPACE_RESET_TAB_REQUESTED'),
      filter(action => action.workspace === 'essential'),
      withLatestFrom(state$),
      map(([{ tabId, productType, productSubType, wrapper, selectedOnBehalfClient }]) => ({
        tabId,
        tradeDate: getTodayStringInLocalTimezone(),
        productType,
        productSubType,
        wrapper,
        selectedOnBehalfClient,
      })),
    );

    const tabId$ = merge(productSelectedTabId$, resetTabId$);

    return tabId$.pipe(
      withLatestFrom(state$),
      mergeMap(([request]) => {
        const { tabId } = request;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const apiCall = essentialFormApiConnector(httpClient).initForm(request);

        return apiCall.pipe(
          takeUntil(tabRemoved$),
          mergeMap(
            ({
              productView,
              blocksMetadata,
              fieldsMetadata,
              values,
              version,
              negotiationId,
              actions,
              quoteQuery,
              errors,
              mustForceAdvancedMode,
              sendWholeFormData,
              allowedRequests,
              productLayout,
            }) =>
              of(
                defaultFormReceivedThunk(
                  tabId,
                  productView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  version,
                  negotiationId,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                  sendWholeFormData,
                  productLayout,
                ),
              ),
          ),
          catchError((error: HttpError) =>
            of(
              commonFormActions.defaultFormFailed(
                'essential',
                tabId,
                error.message || 'default form failed',
                error.traceId,
              ),
            ),
          ),
          timeoutWith(timeoutInMillis, of(commonFormActions.defaultFormTimeouted('essential', tabId)), appScheduler),
        );
      }),
    );
  };
}
